<template>
  <div class="ec-carregamento">
    <!-- Header -->
    <div class="ec-header">
      <div class="ec-header__left">
        <b-button variant="link" class="ec-back-btn" @click="$router.push('/expedicao/volumes/listagem')">
          <i class="fa fa-arrow-left"></i>
        </b-button>
        <div class="ec-header__icon">
          <i class="fa fa-truck"></i>
        </div>
        <div>
          <h1 class="ec-header__title">Pedido #{{ pedidoId }}</h1>
          <p class="ec-header__subtitle">
            <b-badge :variant="situacaoVariant(venda.SITUACAO)" class="mr-2">{{ situacaoTexto(venda.SITUACAO) }}</b-badge>
            {{ venda.NOME_CLIENTE || '...' }}
          </p>
        </div>
      </div>
      <div class="ec-header__actions">
        <b-button
          variant="outline-info"
          size="sm"
          class="mr-2"
          @click="salvarQuantidades"
          :disabled="salvando || fechando || !podeEditar"
        >
          <i class="fa fa-save" :class="{'fa-spin fa-refresh': salvando}"></i>
          Salvar
        </b-button>
        <b-button
          variant="success"
          size="sm"
          @click="confirmarFechamento"
          :disabled="salvando || fechando || !podeEditar"
        >
          <i class="fa fa-check-circle" :class="{'fa-spin fa-refresh': fechando}"></i>
          Fechar Pedido
        </b-button>
      </div>
    </div>

    <!-- Resumo do Pedido -->
    <div class="ec-resumo" v-if="venda.SEQUENCIA">
      <div class="ec-resumo__card">
        <div class="ec-resumo__label">Data do Pedido</div>
        <div class="ec-resumo__value">{{ formatarData(venda.DT_VENDA) }}</div>
      </div>
      <div class="ec-resumo__card">
        <div class="ec-resumo__label">Total Pedido</div>
        <div class="ec-resumo__value ec-resumo__value--blue">{{ formatarQuant(totalPedido) }}</div>
      </div>
      <div class="ec-resumo__card">
        <div class="ec-resumo__label">Total Embarcado</div>
        <div class="ec-resumo__value ec-resumo__value--green">{{ formatarQuant(totalEmbarcado) }}</div>
      </div>
      <div class="ec-resumo__card">
        <div class="ec-resumo__label">Progresso</div>
        <div class="ec-resumo__value">
          <b-progress :value="progresso" :max="100" :variant="progressoVariant" height="10px" class="ec-prog" />
          <small>{{ progresso }}%</small>
        </div>
      </div>
    </div>

    <!-- Tabela de Itens -->
    <div class="ec-itens-wrapper">
      <b-overlay :show="carregando" rounded>
        <div class="ec-itens-header">
          <h5 class="ec-itens-title">Itens do Pedido</h5>
          <small class="ec-itens-hint" v-if="podeEditar">
            <i class="fa fa-info-circle"></i>
            Informe a quantidade embarcada para cada item. Não pode exceder a quantidade pedida.
          </small>
        </div>

        <b-table
          :items="itens"
          :fields="campos"
          hover responsive striped show-empty
          empty-text="Nenhum item encontrado"
          class="ec-table"
          @row-clicked="toggleLotes"
        >
          <template #cell(ITEM)="row">
            <div class="d-flex align-items-center">
              <b-button v-if="parametrosGerais.OP_EXPEDICAO_POR_LOTE === '1'" 
                        size="sm" variant="link" class="p-0 mr-2 text-white" 
                        @click.stop="row.toggleDetails()">
                <i class="fa" :class="row.detailsShowing ? 'fa-minus-square' : 'fa-plus-square'"></i>
              </b-button>
              {{ row.item.ITEM }}
            </div>
          </template>

          <template #cell(QUANT_PEDIDO)="row">
            <span class="ec-quant ec-quant--pedido">{{ formatarQuant(row.item.QUANT_PEDIDO) }}</span>
          </template>

          <template #cell(QUANT)="row">
            <div class="ec-input-wrap" v-if="podeEditar && parametrosGerais.OP_EXPEDICAO_POR_LOTE !== '1'">
              <b-form-input
                v-model="row.item.QUANT_EDIT"
                type="number"
                step="0.001"
                min="0"
                :max="row.item.QUANT_PEDIDO"
                :state="validarItem(row.item)"
                :class="['ec-input-quant', {'ec-input-quant--ok': Number(row.item.QUANT_EDIT) > 0 && validarItem(row.item) !== false}]"
                @input="onQuantChange(row.item)"
              />
              <b-form-invalid-feedback>
                Excede o pedido ({{ formatarQuant(row.item.QUANT_PEDIDO) }})
              </b-form-invalid-feedback>
            </div>
            <div v-else-if="parametrosGerais.OP_EXPEDICAO_POR_LOTE === '1'" class="text-right">
               <span :class="row.item.QUANT_EDIT > row.item.QUANT_PEDIDO ? 'text-danger font-weight-bold' : 'ec-quant--embarcado'">
                 {{ formatarQuant(row.item.QUANT_EDIT) }}
               </span>
               <small v-if="podeEditar" class="d-block text-muted" style="font-size: 0.7rem">Ajuste nos lotes</small>
            </div>
            <span v-else class="ec-quant ec-quant--embarcado">{{ formatarQuant(row.item.QUANT) }}</span>
          </template>

          <template #cell(STATUS_ITEM)="row">
            <div class="ec-item-status">
              <b-progress
                :value="calcProgressoItem(row.item)"
                :max="100"
                :variant="progressoItemVariant(row.item)"
                height="6px"
                class="ec-prog-item"
              />
              <small :class="progressoItemClass(row.item)">{{ calcProgressoItem(row.item) }}%</small>
            </div>
          </template>

          <template #row-details="row">
            <div class="p-3 bg-dark-soft border-top border-bottom">
              <h6 class="text-info mb-3"><i class="fa fa-list-ul mr-2"></i>Apontamento por Lote</h6>
              <b-row v-if="row.item.lotes && row.item.lotes.length">
                <b-col v-for="lote in row.item.lotes" :key="lote.LOTE" md="6" lg="4" class="mb-3">
                  <div class="p-2 rounded border border-secondary bg-dark shadow-sm">
                    <div class="d-flex justify-content-between mb-1">
                      <small class="text-muted">Lote: <strong>{{ lote.LOTE }}</strong></small>
                      <small class="text-info">Planejado: {{ formatarQuant(lote.QUANT_PLANEJADA) }}</small>
                    </div>
                    <vue-autonumeric
                      v-model="lote.QUANT_CARREGADA"
                      :options="{
                        digitGroupSeparator: '.',
                        decimalCharacter: ',',
                        decimalPlaces: 3,
                        minimumValue: '0',
                        outputFormat: 'number'
                      }"
                      class="form-control form-control-sm bg-dark text-white border-info"
                      @input="onLoteQuantChange(row.item)"
                      :disabled="!podeEditar"
                    ></vue-autonumeric>
                  </div>
                </b-col>
              </b-row>
              <div v-else class="text-center p-3 text-muted">
                <i class="fa fa-exclamation-circle mr-2"></i>Nenhum lote vinculado a este item.
              </div>
            </div>
          </template>
        </b-table>
      </b-overlay>
    </div>

    <!-- Modal confirmação fechamento -->
    <b-modal
      id="modal-fechar"
      title="Confirmar Fechamento"
      ok-variant="success"
      ok-title="Fechar Pedido"
      cancel-title="Cancelar"
      @ok="fecharPedido"
      centered
    >
      <div class="ec-modal-body">
        <p>Deseja fechar o pedido <strong>#{{ pedidoId }}</strong>?</p>
        <ul class="ec-modal-resumo">
          <li>Total Pedido: <strong>{{ formatarQuant(totalPedido) }}</strong></li>
          <li>Total Embarcado: <strong>{{ formatarQuant(totalEmbarcado) }}</strong></li>
          <li>Progresso: <strong>{{ progresso }}%</strong></li>
        </ul>
        <b-alert show variant="warning" v-if="progresso < 100">
          <i class="fa fa-exclamation-triangle"></i>
          Atenção: o pedido será fechado com <strong>{{ 100 - progresso }}%</strong> da quantidade ainda não embarcada.
        </b-alert>
        <p class="text-muted">O estoque será baixado e o pedido ficará com status <strong>Embarcado</strong>.</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import VueAutonumeric from 'vue-autonumeric'

export default {
  name: 'ExpedicaoVolumesCarregamento',
  components: {
    VueAutonumeric
  },
  props: {
    pedidoId: { type: [String, Number], required: true }
  },
  data () {
    return {
      carregando: false,
      salvando: false,
      fechando: false,
      venda: {},
      itens: [],
      campos: [
        { key: 'ITEM', label: '#', sortable: false, thClass: 'ec-th', tdClass: 'ec-td ec-td--center' },
        { key: 'COD_PRO', label: 'Cód', thClass: 'ec-th', tdClass: 'ec-td ec-td--mono' },
        { key: 'NOME_PRODUTO', label: 'Produto', thClass: 'ec-th', tdClass: 'ec-td' },
        { key: 'UNIDADE', label: 'Un', thClass: 'ec-th ec-th--center', tdClass: 'ec-td ec-td--center' },
        { key: 'QUANT_PEDIDO', label: 'Qtd Pedida', thClass: 'ec-th ec-th--right', tdClass: 'ec-td text-right' },
        { key: 'QUANT', label: 'Qtd Embarcada', thClass: 'ec-th ec-th--right', tdClass: 'ec-td' },
        { key: 'STATUS_ITEM', label: 'Progresso', thClass: 'ec-th', tdClass: 'ec-td' }
      ],
      parametrosGerais: {
        OP_EXPEDICAO_POR_LOTE: '0'
      }
    }
  },
  computed: {
    podeEditar () {
      return ['4', '5'].includes(String(this.venda.SITUACAO || '').trim())
    },
    totalPedido () {
      return this.itens.reduce((s, i) => s + Number(i.QUANT_PEDIDO || 0), 0)
    },
    totalEmbarcado () {
      return this.itens.reduce((s, i) => s + Number(i.QUANT_EDIT ?? i.QUANT ?? 0), 0)
    },
    progresso () {
      if (this.totalPedido === 0) return 0
      return Math.min(100, Math.round((this.totalEmbarcado / this.totalPedido) * 100))
    },
    progressoVariant () {
      if (this.progresso >= 100) return 'success'
      if (this.progresso >= 50) return 'warning'
      return 'info'
    }
  },
  async created () {
    await this.carregarParametros()
    this.carregarDados()
  },
  methods: {
    carregarParametros() {
      return this.$http.get('/api/protected/parametro/getDados')
        .then(res => {
          if (res.data && res.data.length > 0) {
            const p = res.data[0]
            this.parametrosGerais.OP_EXPEDICAO_POR_LOTE = String(p.OP_EXPEDICAO_POR_LOTE || '0').trim()
          }
        })
        .catch(err => {
          console.error('Erro ao carregar parâmetros:', err)
        })
    },
    toggleLotes(item) {
      if (this.parametrosGerais.OP_EXPEDICAO_POR_LOTE !== '1') return
      item._showDetails = !item._showDetails
    },
    onLoteQuantChange(item) {
      // Atualizar total do item baseado na soma dos lotes
      item.QUANT_EDIT = item.lotes.reduce((acc, l) => acc + Number(l.QUANT_CARREGADA || 0), 0)
      if (item.QUANT_EDIT > item.QUANT_PEDIDO) {
        // Bloquear ou avisar? O usuário pediu para que a quantidade seja apontada nos lotes
        // Se exceder, vamos avisar na validação
      }
    },
    async carregarDados () {
      this.carregando = true
      try {
        const result = await this.$http.get(`/api/protected/terminal/expedicao/volumes/getItensPedido/${this.pedidoId}`)
        this.venda = result.data.venda || {}
        // Processar itens (injetar reatividade e somar lotes se houver)
        let temLotes = false
        this.itens = (result.data.itens || []).map(i => {
          const item = {
            ...i,
            QUANT_EDIT: 0,
            _showDetails: false,
            lotes: (i.lotes || []).map(l => ({
              ...l,
              QUANT_CARREGADA: Number(l.QUANT_CARREGADA || 0)
            }))
          }

          if (item.lotes.length > 0) temLotes = true

          // Se houver lotes, a quantidade embarcada é a soma deles
          if (item.lotes.length > 0) {
            item.QUANT_EDIT = item.lotes.reduce((acc, l) => acc + Number(l.QUANT_CARREGADA || 0), 0)
          } else {
            item.QUANT_EDIT = i.QUANT != null ? Number(i.QUANT) : 0
          }
          
          return item
        })

        // Forçar ativação do modo lote se houver dados de lote (segurança contra falha no parametro)
        if (temLotes) {
          this.parametrosGerais.OP_EXPEDICAO_POR_LOTE = '1'
        }

        // Se pedido ainda está em status 4, inicia carregamento automaticamente
        if (String(this.venda.SITUACAO || '').trim() === '4') {
          await this.iniciarCarregamento()
        }
      } catch (err) {
        this.$bvToast.toast('Erro ao carregar dados: ' + (err.response?.data || err.message), {
          title: 'Erro', variant: 'danger', autoHideDelay: 5000
        })
        this.$router.push('/expedicao/volumes/listagem')
      } finally {
        this.carregando = false
      }
    },
    async iniciarCarregamento () {
      try {
        await this.$http.post('/api/protected/terminal/expedicao/volumes/iniciarCarregamento', {
          pedidoId: this.pedidoId,
          usuario: window.localStorage.getItem('login')
        })
        this.venda.SITUACAO = '5'
      } catch (err) {
        // silencioso — pode já estar em status 5
      }
    },
    async salvarQuantidades () {
      if (!this.podeEditar) return
      if (!this.validarTodos()) return

      this.salvando = true
      try {
        const itensPayload = this.itens.map(i => ({
          CONTROLE: i.CONTROLE,
          QUANT: Number(i.QUANT_EDIT ?? 0),
          QUANT_PEDIDO: Number(i.QUANT_PEDIDO || 0),
          lotes: i.lotes
        }))

        await this.$http.post('/api/protected/terminal/expedicao/volumes/salvarQuantidades', {
          pedidoId: this.pedidoId,
          itens: itensPayload
        })

        this.$bvToast.toast('Quantidades salvas com sucesso!', {
          title: 'Sucesso', variant: 'success', autoHideDelay: 2500
        })
      } catch (err) {
        this.$bvToast.toast('Erro ao salvar: ' + (err.response?.data || err.message), {
          title: 'Erro', variant: 'danger', autoHideDelay: 5000
        })
      } finally {
        this.salvando = false
      }
    },
    confirmarFechamento () {
      if (!this.validarTodos()) return
      this.$bvModal.show('modal-fechar')
    },
    async fecharPedido () {
      this.fechando = true
      try {
        // Salva quantidades antes de fechar
        const itensPayload = this.itens.map(i => ({
          CONTROLE: i.CONTROLE,
          QUANT: Number(i.QUANT_EDIT ?? 0),
          QUANT_PEDIDO: Number(i.QUANT_PEDIDO || 0),
          lotes: i.lotes
        }))
        await this.$http.post('/api/protected/terminal/expedicao/volumes/salvarQuantidades', {
          pedidoId: this.pedidoId,
          itens: itensPayload
        })

        // Fecha o pedido (baixa estoque + SITUACAO = 6)
        await this.$http.post('/api/protected/terminal/expedicao/volumes/fecharPedido', {
          pedidoId: this.pedidoId,
          usuario: window.localStorage.getItem('login')
        })

        this.$bvToast.toast(`Pedido #${this.pedidoId} fechado com sucesso!`, {
          title: 'Embarcado', variant: 'success', autoHideDelay: 3000
        })

        setTimeout(() => this.$router.push('/expedicao/volumes/listagem'), 1500)
      } catch (err) {
        this.$bvToast.toast('Erro ao fechar pedido: ' + (err.response?.data || err.message), {
          title: 'Erro', variant: 'danger', autoHideDelay: 6000
        })
      } finally {
        this.fechando = false
      }
    },
    onQuantChange (item) {
      const v = Number(item.QUANT_EDIT)
      if (v > Number(item.QUANT_PEDIDO)) {
        item.QUANT_EDIT = Number(item.QUANT_PEDIDO)
      }
    },
    validarItem (item) {
      const quant = Number(item.QUANT_EDIT)
      const pedido = Number(item.QUANT_PEDIDO)
      if (quant < 0) return false
      if (quant > pedido) return false
      return null // null = sem indicação (nem válido nem inválido)
    },
    validarTodos () {
      for (const item of this.itens) {
        if (this.validarItem(item) === false) {
          this.$bvToast.toast('Corrija as quantidades inválidas antes de prosseguir', {
            title: 'Atenção', variant: 'warning', autoHideDelay: 4000
          })
          return false
        }
      }
      return true
    },
    calcProgressoItem (item) {
      const pedido = Number(item.QUANT_PEDIDO || 0)
      if (pedido === 0) return 0
      const emb = Number(item.QUANT_EDIT ?? item.QUANT ?? 0)
      return Math.min(100, Math.round((emb / pedido) * 100))
    },
    progressoItemVariant (item) {
      const p = this.calcProgressoItem(item)
      if (p >= 100) return 'success'
      if (p > 0) return 'warning'
      return 'secondary'
    },
    progressoItemClass (item) {
      const p = this.calcProgressoItem(item)
      if (p >= 100) return 'text-success'
      if (p > 0) return 'text-warning'
      return 'text-muted'
    },
    situacaoTexto (s) {
      const m = { '4': 'Em Ordem', '5': 'Embarcando', '6': 'Embarcado' }
      return m[String(s || '').trim()] || s
    },
    situacaoVariant (s) {
      const m = { '4': 'info', '5': 'warning', '6': 'success' }
      return m[String(s || '').trim()] || 'secondary'
    },
    formatarQuant (v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 })
    },
    formatarData (v) {
      if (!v) return '-'
      return new Date(v).toLocaleDateString('pt-BR')
    }
  }
}
</script>

<style scoped>
.ec-carregamento {
  min-height: 100vh;
  background: linear-gradient(135deg, #0f1923 0%, #1a2639 50%, #0f1923 100%);
  padding: 24px;
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

.ec-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 20px; padding: 16px 20px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px; backdrop-filter: blur(10px);
  gap: 12px; flex-wrap: wrap;
}

.ec-back-btn { color: rgba(255,255,255,0.6); padding: 4px 8px; }
.ec-back-btn:hover { color: #fff; }

.ec-header__left { display: flex; align-items: center; gap: 12px; }

.ec-header__icon {
  width: 44px; height: 44px;
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; color: #fff;
  box-shadow: 0 4px 16px rgba(239,68,68,0.4);
}

.ec-header__title { font-size: 1.4rem; font-weight: 700; color: #fff; margin: 0; }
.ec-header__subtitle { color: rgba(255,255,255,0.5); font-size: 0.85rem; margin: 0; display: flex; align-items: center; }
.ec-header__actions { display: flex; gap: 8px; flex-wrap: wrap; }

/* Resumo */
.ec-resumo {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px; margin-bottom: 20px;
}

.ec-resumo__card {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px; padding: 14px 18px;
}

.ec-resumo__label { color: rgba(255,255,255,0.5); font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
.ec-resumo__value { color: #fff; font-size: 1.1rem; font-weight: 600; }
.ec-resumo__value--blue { color: #60a5fa; }
.ec-resumo__value--green { color: #34d399; }
.ec-prog { border-radius: 4px; }

/* Itens */
.ec-itens-wrapper {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 16px; overflow: hidden;
}

.ec-itens-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px; border-bottom: 1px solid rgba(255,255,255,0.07);
}

.ec-itens-title { color: #e2e8f0; font-size: 1rem; font-weight: 600; margin: 0; }
.ec-itens-hint { color: rgba(255,255,255,0.4); font-size: 0.8rem; }

.ec-table { background: transparent; color: #e2e8f0; }
.ec-th { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.7) !important; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.04em; border: none !important; }
.ec-th--right { text-align: right; }
.ec-th--center { text-align: center; }
.ec-td { border-top: 1px solid rgba(255,255,255,0.05) !important; vertical-align: middle; }
.ec-td--mono { font-family: monospace; font-weight: 600; }
.ec-td--center { text-align: center; }

.ec-quant { font-variant-numeric: tabular-nums; }
.ec-quant--pedido { color: #93c5fd; }
.ec-quant--embarcado { color: #6ee7b7; }

.ec-input-wrap { max-width: 140px; }
.ec-input-quant {
  background: rgba(255,255,255,0.07) !important;
  border: 1px solid rgba(255,255,255,0.15) !important;
  color: #fff !important;
  border-radius: 8px !important;
  text-align: right;
  transition: border-color 0.2s;
}
.ec-input-quant:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 2px rgba(59,130,246,0.25) !important; }
.ec-input-quant--ok { border-color: rgba(52,211,153,0.5) !important; }

.ec-item-status { display: flex; align-items: center; gap: 6px; min-width: 100px; }
.ec-prog-item { flex: 1; border-radius: 3px; background: rgba(255,255,255,0.1); }

.ec-modal-body p { color: #333; }
.ec-modal-resumo { margin: 12px 0; padding-left: 20px; color: #555; }
.ec-modal-resumo li { margin-bottom: 4px; }
</style>



// WEBPACK FOOTER //
// src/views/expedicao/volumes/ExpedicaoVolumesCarregamento.vue